// @flow

import { all, put, takeEvery } from "redux-saga/effects";
import http from "@http";
import { call } from "redux-saga/effects";
import { downloadCsvFile } from "@sagas/utils";
import actionCreator from "@modules/reporting/actionCreator";
import * as actions from "@modules/reporting/constants/actions";
import type { Action } from "redux-action";
import config from "@config";

export function* onGetProvidersReport({ payload }: Object): Generator<void, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.dateRange) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const page = (payload || {}).page || 1;
    let response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/provider-management/providers?dateRange=${payload.dateRange}`,
      {
        params: {
          page,
          limit: config.results50PerPage,
        },
      },
    );

    yield put(actionCreator.GetProvidersReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.GetProvidersReportError(e.response));
  }
}

function* onExportProvidersReport({
  payload,
}: Action<{ clientId: number, dateRange: string }>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.dateRange) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/provider-management/providers/export?dateRange=${payload.dateRange}`,
    );

    downloadCsvFile(response.data, `Providers Report-${payload.dateRange}.csv`);
    yield put(actionCreator.ExportProvidersReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.ExportProvidersReportError(e.response));
  }
}

function* onGetCoverageData({
  payload,
}: Action<{ clientId: number, page?: number }>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const page = (payload || {}).page || 1;
    let response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/provider-management/coverage`,
      {
        params: {
          page,
          limit: config.results50PerPage,
        },
      },
    );

    yield put(actionCreator.GetCoverageDataSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.GetCoverageDataError(e.response));
  }
}

function* onExportCoverageData({ payload }: Action<{ clientId: number }>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/provider-management/coverage/export`,
    );

    downloadCsvFile(response.data, "Coverage Report.csv");
    yield put(actionCreator.ExportCoverageDataSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.ExportCoverageDataError(e.response));
  }
}

function* onGetCoverageChartData({ payload }: Action<{ clientId: number }>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    let response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/provider-management/coverage/chart`,
    );

    yield put(actionCreator.GetCoverageChartDataSuccess(response.data));
  } catch (e) {
    yield put(actionCreator.GetCoverageChartDataError(e.response));
  }
}

export function* ProvidersReportSaga(): Generator<*, void, void> {
  yield all([takeEvery(actions.GetProvidersReport, onGetProvidersReport)]);
  yield all([takeEvery(actions.ExportProvidersReport, onExportProvidersReport)]);
  yield all([takeEvery(actions.GetCoverageData, onGetCoverageData)]);
  yield all([takeEvery(actions.ExportCoverageData, onExportCoverageData)]);
  yield all([takeEvery(actions.GetCoverageChartData, onGetCoverageChartData)]);
}

export default ProvidersReportSaga;
